import { useCallback, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ref, getDownloadURL } from 'firebase/storage'
import { fetchJobs, retryJob, removeJob, cancelJob } from '../lib/api.js'
import { storage } from '../lib/firebase.js'
import { useAuth } from '../hooks/useAuth.js'
import { usePlayer } from '../hooks/usePlayer.js'

interface JobsPanelProps {
  onNewDub: () => void
}

export default function JobsPanel({ onNewDub }: JobsPanelProps) {
  const { t } = useTranslation()
  const { user } = useAuth()

  const { data } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => fetchJobs(20),
    enabled: !!user,
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? []
      const hasActive = jobs.some(j => !['ready', 'failed', 'expired'].includes(j.status))
      return hasActive ? 3000 : false
    },
  })

  const jobs = data?.jobs ?? []
  const activeCount = jobs.filter(j => !['ready', 'failed', 'expired', 'queued'].includes(j.status)).length
  const queuedCount = jobs.filter(j => j.status === 'queued').length

  const badgeText = activeCount > 0
    ? `${activeCount} ${t('shell.active')}`
    : queuedCount > 0
      ? `${queuedCount} ${t('shell.queued')}`
      : ''

  return (
    <aside className="jobs-panel">
      <div className="jp-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span className="jp-title">{t('shell.dubQueue')}</span>
          {badgeText && <span className="jp-badge">{badgeText}</span>}
        </div>
        <button className="sidebar-btn" style={{ width: 26, height: 26 }} onClick={onNewDub}>
          <svg viewBox="0 0 24 24" width="13" height="13"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
        </button>
      </div>

      <div className="jp-scroll">
        {jobs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 12px', fontSize: 12, color: 'var(--muted-app)' }}>
            {t('shell.noJobs')}
          </div>
        ) : (
          jobs.map(job => (
            <JobCardShell key={job.id} job={job} />
          ))
        )}
      </div>

      <div className="jp-footer">
        <div className="jp-status">
          <div className="jp-pulse" />
          {t('shell.allSystems')}
        </div>
        <span style={{ fontSize: 10, color: 'var(--muted-app)' }}>v1.0.0</span>
      </div>
    </aside>
  )
}

/* Compact job card for sidebar */
function JobCardShell({ job }: {
  job: {
    id: string
    status: string
    videoTitle?: string
    videoUrl?: string
    targetLang: string
    sourceLang?: string
    videoThumbnailUrl?: string
    durationSeconds?: number
    outputUrl?: string | null
    outputStoragePath?: string | null
    originalStoragePath?: string | null
  }
}) {
  const { t } = useTranslation()
  const player = usePlayer()
  const qc = useQueryClient()
  const [downloading, setDownloading] = useState(false)
  const [retrying, setRetrying] = useState(false)

  const isDone = job.status === 'ready'

  // Resolve storage path → download URL for done jobs
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!isDone) return
    const path = job.outputUrl ?? job.outputStoragePath ?? null
    if (!path) return
    if (path.startsWith('http')) { setAudioUrl(path); return }
    getDownloadURL(ref(storage, path))
      .then(url => setAudioUrl(url))
      .catch(() => {})
  }, [isDone, job.outputUrl, job.outputStoragePath])

  const statusClass = ['capturing'].includes(job.status) ? 'cap'
    : isDone ? 'done'
      : ['queued'].includes(job.status) ? 'q'
        : 'proc'

  const isActive = !['ready', 'failed', 'expired', 'queued'].includes(job.status)
  const isQueued = job.status === 'queued'

  const variant = `v${(parseInt(job.id.slice(-2), 16) % 6) + 1}`
  const title = job.videoTitle || t('job.untitled')
  const langPair = `${(job.sourceLang ?? 'Auto').toUpperCase()} → ${job.targetLang.toUpperCase()}`

  const handleWatch = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (!audioUrl) return
    player.openMiniPlayer(job.id, audioUrl, {
      videoTitle: job.videoTitle ?? t('job.untitled'),
      videoUrl: job.videoUrl ?? '',
      videoThumbnailUrl: job.videoThumbnailUrl ?? '',
      targetLang: job.targetLang,
      sourceLang: job.sourceLang ?? 'auto',
      durationSeconds: job.durationSeconds ?? 0,
      originalStoragePath: job.originalStoragePath ?? null,
    })
  }, [job, player, audioUrl])

  const handleAudio = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!audioUrl) return
    setDownloading(true)
    try {
      const res = await fetch(audioUrl)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${job.videoTitle ?? 'dubbed'}.mp3`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }, [audioUrl, job.videoTitle])

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!audioUrl) return
    if (navigator.share) {
      try {
        await navigator.share({ title: job.videoTitle ?? t('shell.dubbed'), url: audioUrl })
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(audioUrl)
    }
  }, [audioUrl, job.videoTitle])

  const handleRetry = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    setRetrying(true)
    try {
      await retryJob(job.id)
      void qc.invalidateQueries({ queryKey: ['jobs'] })
    } catch { /* show nothing — will stay failed */ }
    finally { setRetrying(false) }
  }, [job.id, qc])

  const handleDelete = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await removeJob(job.id)
      void qc.invalidateQueries({ queryKey: ['jobs'] })
    } catch { /* ignore */ }
  }, [job.id, qc])

  const handleCancel = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await cancelJob(job.id)
      void qc.invalidateQueries({ queryKey: ['jobs'] })
    } catch { /* ignore */ }
  }, [job.id, qc])

  const isFailed = job.status === 'failed'

  return (
    <div className={`job-card-shell${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}${isQueued ? ' is-queued' : ''}`} onClick={isDone && audioUrl ? handleWatch : undefined} style={{ cursor: isDone && audioUrl ? 'pointer' : 'default' }}>
      <div className="jc-top">
        <div className="jc-thumb">
          {job.videoThumbnailUrl?.startsWith('http') ? (
            <img src={job.videoThumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div className={`dub-thumb-bg ${variant}`} style={{ width: '100%', height: '100%' }} />
          )}
        </div>
        <div className="jc-info">
          <div className="jc-info-title">{title}</div>
          <div className="jc-info-lang">{langPair}</div>
        </div>
        <div className={`jc-status ${statusClass}`}>
          {isActive && <i />}
          {isDone && '✓ '}
          {t(`job.statusShort.${job.status}`, job.status)}
        </div>
      </div>
      {isDone && audioUrl && (
        <div className="jc-acts">
          <button className="jca p" onClick={handleWatch}>
            <svg viewBox="0 0 24 24"><polygon points="8 5 19 12 8 19" fill="currentColor" stroke="none" /></svg>
            {t('job.watchBtn')}
          </button>
          <button className="jca" onClick={e => void handleAudio(e)} disabled={downloading}>
            <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            {downloading ? '...' : t('job.audioBtn')}
          </button>
          <button className="jca" onClick={e => void handleShare(e)}>
            <svg viewBox="0 0 24 24"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
            {t('job.shareBtn')}
          </button>
        </div>
      )}
      {(isActive || isQueued) && (
        <div className="jc-acts">
          <button className="jca" onClick={e => void handleCancel(e)}>
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
            {t('job.cancelBtn', 'Cancel')}
          </button>
        </div>
      )}
      {isFailed && (
        <div className="jc-acts">
          <button className="jca p" onClick={e => void handleRetry(e)} disabled={retrying}>
            <svg viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" /></svg>
            {retrying ? '...' : t('job.retryBtn', 'Retry')}
          </button>
          <button className="jca" onClick={e => void handleDelete(e)}>
            <svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
            {t('job.deleteBtn', 'Delete')}
          </button>
        </div>
      )}
    </div>
  )
}
